import { useState, useEffect, useRef } from 'react';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';

interface UseSpeechRecognitionReturn {
  isListening: boolean;
  transcript: string;
  error: string | null;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
}

/**
 * Custom hook for speech recognition functionality
 * Handles browser compatibility, permissions, and speech-to-text conversion
 */
export const useSpeechRecognitionHook = (): UseSpeechRecognitionReturn => {
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const listeningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use react-speech-recognition hook
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  // Check browser support
  useEffect(() => {
    if (browserSupportsSpeechRecognition) {
      setIsSupported(true);
      setError(null);
    } else {
      setIsSupported(false);
      setError(
        'Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.'
      );
    }
  }, [browserSupportsSpeechRecognition]);

  const finalIsSupported = isSupported && browserSupportsSpeechRecognition;

  const startListening = () => {
    if (!finalIsSupported) {
      setError(
        'Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.'
      );
      return;
    }

    setError(null);

    try {
      // Start listening with continuous mode for real-time transcription
      SpeechRecognition.startListening({
        continuous: true,
        language: navigator.language || 'en-US',
      }).catch((err: any) => {
        console.error('Error starting speech recognition:', err);
        // Handle permission errors
        if (err?.message?.includes('permission') || err?.name === 'NotAllowedError') {
          setError(
            'Microphone access denied. Please allow microphone access in your browser settings.'
          );
        } else if (err?.message?.includes('microphone') || err?.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone.');
        } else {
          setError(
            'Failed to start speech recognition. Please check microphone permissions.'
          );
        }
      });

      // Clear any existing timeout
      if (listeningTimeoutRef.current) {
        clearTimeout(listeningTimeoutRef.current);
        listeningTimeoutRef.current = null;
      }
    } catch (err: any) {
      console.error('Error starting speech recognition:', err);
      setError(
        'Failed to start speech recognition. Please check microphone permissions.'
      );
    }
  };

  const stopListening = () => {
    try {
      SpeechRecognition.stopListening();

      // Clear timeout if exists
      if (listeningTimeoutRef.current) {
        clearTimeout(listeningTimeoutRef.current);
        listeningTimeoutRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping speech recognition:', err);
    }
  };

  // Handle speech recognition errors via error boundary
  useEffect(() => {
    // The library handles errors internally, but we can catch permission errors
    if (!listening && transcript === '' && isSupported) {
      // Reset error when not listening and no transcript
      setError(null);
    }
    
    return () => {
      if (listeningTimeoutRef.current) {
        clearTimeout(listeningTimeoutRef.current);
      }
    };
  }, [listening, transcript, isSupported]);

  return {
    isListening: listening,
    transcript,
    error,
    isSupported: finalIsSupported,
    startListening,
    stopListening,
    resetTranscript,
  };
};

